import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { useAppSelector } from '../../../redux/hooks'
import { selectIsTrialExpired } from '../../../utils/is-trial-expired'
import './lc-trial-expiration-overlay.scss'

interface ILCTrialExpirationOverlayProps {
  children: React.ReactNode
}

// Routes that are allowed during trial expiration (same as in LCTrialGuard)
const ALLOWED_ROUTES_DURING_TRIAL_EXPIRATION = ['/app/purchase']

// Support email is a brand address. Never pass it through i18n — translators
// previously appended "(This is an email address and should not be translated.)"
// into other locales (ECOMM-3202).
export const TRIAL_SUPPORT_EMAIL = 'support@logicalcommander.com'

export const LCTrialExpirationOverlay: FC<ILCTrialExpirationOverlayProps> = ({ children }) => {
  const { t } = useTranslation('e-commander-common', { keyPrefix: 'trial.expiration' })
  const isTrialExpired = useAppSelector(selectIsTrialExpired)
  const location = useLocation()

  const isCurrentRouteAllowed = useMemo(() => {
    return ALLOWED_ROUTES_DURING_TRIAL_EXPIRATION.some((route) => location.pathname.startsWith(route))
  }, [location.pathname])

  // Don't show overlay if trial is not expired
  if (!isTrialExpired) {
    return <>{children}</>
  }

  // Don't show overlay if current route is allowed (purchase/settings)
  if (isCurrentRouteAllowed) {
    return <>{children}</>
  }

  return (
    <div className="lc-trial-expiration-overlay">
      <div className="lc-trial-expiration-overlay__content">
        <div className="lc-trial-expiration-overlay__header">
          <div className="lc-trial-expiration-overlay__icon">⏰</div>
          <h1 className="lc-trial-expiration-overlay__title">{t('title')}</h1>
          <p className="lc-trial-expiration-overlay__subtitle">{t('subtitle')}</p>
        </div>

        <div className="lc-trial-expiration-overlay__body">
          <p className="lc-trial-expiration-overlay__description">{t('description')}</p>

          <div className="lc-trial-expiration-overlay__features">
            <h3>{t('features.title')}</h3>
            <ul>
              <li>{t('features.purchase')}</li>
              <li>{t('features.contact')}</li>
            </ul>
          </div>
        </div>

        <div className="lc-trial-expiration-overlay__actions">
          <Link to="/app/purchase/risk-hr" className="lc-trial-expiration-overlay__button lc-trial-expiration-overlay__button--primary">
            {t('actions.purchase')}
          </Link>
        </div>

        <div className="lc-trial-expiration-overlay__footer">
          <p className="lc-trial-expiration-overlay__help">
            {t('help.text')}{' '}
            <a href={`mailto:${TRIAL_SUPPORT_EMAIL}`} className="lc-trial-expiration-overlay__link">
              {TRIAL_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>

      {/* Semi-transparent background that shows the app underneath */}
      <div className="lc-trial-expiration-overlay__background">{children}</div>
    </div>
  )
}
